import { useCallback } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { DurationInput } from '@/components/organisms/DurationInput/DurationInput';
import { Form, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useIntegrations } from '@/hooks/useIntegrations';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  InstagramIntegration,
  IntegrationType,
  WhatsAppIntegration,
} from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { COEXISTENCE_TIMEOUT } from './constants';

type CoexistenceIntegration = WhatsAppIntegration | InstagramIntegration;

type CoexistenceForm = {
  is_coexistence: boolean;
  coexistence_timeout_enabled: boolean;
  coexistence_timeout: number;
  coexistence_reset_text?: string;
};

interface Props {
  integration: CoexistenceIntegration;
}

const FORM_ID = 'whatsapp-coexistence-form';

const CoexistenceCard = ({ integration }: Props) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const { updateIntegration, updateStatus } = useIntegrations<CoexistenceIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  // WhatsApp sets is_coexistence during onboarding, Instagram uses the switch below.
  const isWhatsapp = integration?.type === IntegrationType.WHATSAPP;
  const serverIsCoexistence = integration?.config?.is_coexistence ?? false;
  const hasCustomTimeout = !!integration?.config?.coexistence_timeout;
  // The API rejects an Instagram reset command over 64 characters.
  const resetTextMaxLength = isWhatsapp ? 100 : 64;

  const form = useForm<CoexistenceForm>({
    mode: 'onChange',
    defaultValues: {
      is_coexistence: serverIsCoexistence,
      coexistence_timeout_enabled: hasCustomTimeout,
      // Convert minutes to seconds for DurationInput
      coexistence_timeout:
        (integration?.config?.coexistence_timeout ?? 0) * 60 || COEXISTENCE_TIMEOUT.DEFAULT_SECONDS,
      coexistence_reset_text: integration?.config?.coexistence_reset_text || '',
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    resetField,
    formState: { errors },
  } = form;

  const isCoexistence = useWatch({ control, name: 'is_coexistence' });
  const timeoutEnabled = useWatch({ control, name: 'coexistence_timeout_enabled' });
  const timeoutValue = useWatch({ control, name: 'coexistence_timeout' });

  const handleTimeoutToggle = useCallback(
    (enabled: boolean) => {
      setValue('coexistence_timeout_enabled', enabled, { shouldDirty: true });
      if (enabled && !timeoutValue) {
        setValue('coexistence_timeout', COEXISTENCE_TIMEOUT.DEFAULT_SECONDS);
      }
    },
    [setValue, timeoutValue]
  );

  const onSubmit = (values: CoexistenceForm) => {
    const updatedConfig = { ...integration.config, is_coexistence: values.is_coexistence };

    // Convert seconds back to minutes, only set if enabled
    if (values.coexistence_timeout_enabled && values.coexistence_timeout > 0) {
      updatedConfig.coexistence_timeout = Math.round(values.coexistence_timeout / 60);
    } else {
      delete updatedConfig.coexistence_timeout;
    }

    if (values.coexistence_reset_text?.trim()) {
      updatedConfig.coexistence_reset_text = values.coexistence_reset_text.trim();
    } else {
      delete updatedConfig.coexistence_reset_text;
    }

    updateIntegration({ ...integration, config: updatedConfig });
  };

  // Calculate dirty state
  const serverHasCustomTimeout = !!integration?.config?.coexistence_timeout;
  const serverTimeout = (integration?.config?.coexistence_timeout ?? 0) * 60;
  const serverResetText = integration?.config?.coexistence_reset_text || '';
  const currentResetText = useWatch({ control, name: 'coexistence_reset_text' }) || '';

  const isTimeoutDirty =
    timeoutEnabled !== serverHasCustomTimeout || (timeoutEnabled && timeoutValue !== serverTimeout);
  const isResetTextDirty = currentResetText !== serverResetText;

  const isDirty = isCoexistence !== serverIsCoexistence || isTimeoutDirty || isResetTextDirty;
  const isSaveDisabled = !isDirty || !canWrite;

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <div className="w-full">
          <div className="mb-2 flex items-center justify-between">
            <p className="px-1.5 text-sm leading-none font-medium text-muted-foreground">
              {t('integrations.whatsapp.coexistence.title')}
            </p>
          </div>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateStatus === 'pending'}
            disabled={isSaveDisabled}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-6">
              {!isWhatsapp && (
                <Controller
                  name="is_coexistence"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      variant="description"
                      switchPosition="right"
                      label={t('integrations.facebook.coexistence.enable.title')}
                      description={t('integrations.facebook.coexistence.enable.subtitle')}
                      checked={value}
                      onCheckedChange={(checked) => {
                        onChange(checked);
                        // Drop the hidden reset field's unsaved edit so it cannot block the save.
                        if (!checked) {
                          resetField('coexistence_reset_text', { defaultValue: serverResetText });
                        }
                      }}
                      disabled={!canWrite}
                    />
                  )}
                />
              )}

              {/* Coexistence Timeout Section */}
              <div className={cn('flex flex-col gap-4', !isCoexistence && 'hidden')}>
                <Controller
                  name="coexistence_timeout_enabled"
                  control={control}
                  render={({ field: { value } }) => (
                    <Switch
                      variant="description"
                      switchPosition="right"
                      label={
                        isWhatsapp
                          ? t('integrations.whatsapp.coexistence.timeout.title')
                          : t('integrations.facebook.coexistence.timeout.title')
                      }
                      description={
                        isWhatsapp
                          ? t('integrations.whatsapp.coexistence.timeout.subtitle_toggle')
                          : t('integrations.facebook.coexistence.timeout.subtitle_toggle')
                      }
                      checked={value}
                      onCheckedChange={handleTimeoutToggle}
                      disabled={!canWrite}
                    />
                  )}
                />
                {timeoutEnabled && (
                  <Controller
                    name="coexistence_timeout"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <DurationInput
                        value={value || COEXISTENCE_TIMEOUT.DEFAULT_SECONDS}
                        onChange={onChange}
                        disabled={!canWrite}
                        minSeconds={COEXISTENCE_TIMEOUT.MIN_SECONDS}
                        maxSeconds={
                          isWhatsapp
                            ? COEXISTENCE_TIMEOUT.MAX_SECONDS
                            : COEXISTENCE_TIMEOUT.INSTAGRAM_MAX_SECONDS
                        }
                        className="mt-0"
                      />
                    )}
                  />
                )}
              </div>

              {/* Reset Command Section */}
              <div className={cn('space-y-2', !isCoexistence && 'hidden')}>
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {t('integrations.whatsapp.coexistence.reset_text.title')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('integrations.whatsapp.coexistence.reset_text.subtitle')}
                  </p>
                </div>
                <Controller
                  control={control}
                  name="coexistence_reset_text"
                  rules={{
                    maxLength: {
                      value: resetTextMaxLength,
                      message: t('validation.less_than', { 0: resetTextMaxLength }),
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      id="coexistence_reset_text"
                      type="text"
                      placeholder={t('integrations.whatsapp.coexistence.reset_text.placeholder')}
                      disabled={!canWrite}
                      className={cn(
                        'w-full max-w-72',
                        errors.coexistence_reset_text &&
                          'border-destructive focus-visible:ring-destructive'
                      )}
                      {...field}
                      value={field.value ?? ''}
                    />
                  )}
                />
                {errors.coexistence_reset_text?.message && (
                  <FormMessage>{errors.coexistence_reset_text.message}</FormMessage>
                )}
              </div>
            </div>
          </FieldsetCard>
        </div>
      </form>
    </Form>
  );
};

export default CoexistenceCard;
